import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import ClientIcon from "assets/icons/person.svg?react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import workspaceService from "api/workspaceService";
import { IWorkspace, IWorkspaceMember } from "dto/IWorkspace";
import NewInput from "components/NewInput";
import Placeholder from "assets/placeholder.png";
import { useOnClickOutside } from "usehooks-ts";
import CloseIcon from "assets/icons/close.svg?react";
import DropdownLogic from "components/DropdownLogic";
import HollowButton from "components/HollowButton";
import Button from "components/Button";
import NotFoundScreen from "components/NotFoundScreen";
import { useTranslation } from "react-i18next";

const SearchMemberNew: FC<Props> = ({
  isDisabled = false,
  selected,
  onChoose,
  label,
  allowSearch = true,
  showButtons = false,
  showMe = true,
  selectedUserId,
}) => {
  const { t } = useTranslation();
  const [workspaceId] = useState(getWorkspaceId() ?? "");

  const { data: workspaceData, isLoading } =
    workspaceService.useGetWorkspace(workspaceId);

  const workspace: IWorkspace = useMemo(
    () => (workspaceData?.data as IWorkspace) ?? [],
    [workspaceData],
  );

  const workspaceMembers = useMemo(() => workspace?.Members ?? [], [workspace]);

  const [searchedMembers, setSearchedMembers] = useState<IWorkspaceMember[]>(
    [],
  );

  const [selectedInternal, setSelectedInternal] =
    useState<IWorkspaceMember>(null);

  const [searchValue, setSearchValue] = useState("");
  const [active, setActive] = useState(false);

  const outsideRef = useRef(null);

  useOnClickOutside(outsideRef, () => {
    if (active) {
      setActive(false);
    }
  });

  useEffect(() => {
    if (!searchValue.length) {
      setSearchedMembers(workspaceMembers.filter((x) => !x.IsMe || showMe));
      return;
    }

    setSearchedMembers(
      workspaceMembers.filter(
        (m) =>
          m.Label.toLocaleLowerCase().includes(
            searchValue.toLocaleLowerCase(),
          ) &&
          (!m.IsMe || showMe),
      ),
    );
  }, [workspaceMembers, searchValue]);

  useEffect(() => {
    if (selectedUserId && workspaceMembers) {
      setSelectedInternal(
        workspaceMembers.find((x) => x.UserId === selectedUserId),
      );
    } else {
      setSelectedInternal(null);
    }
  }, [workspaceMembers, selectedUserId]);

  const clear = () => {
    onChoose(null);
    setActive(null);
  };

  const save = () => {
    //onChoose(internalSelected);
    setActive(false);
  };

  return (
    <div ref={outsideRef} className={styles.wrapper}>
      <div
        onClick={(e: any) => {
          if (isDisabled) return;
          const ele = e.target.tagName.toLowerCase();
          const button = e.target.outerHTML.search("button") !== -1;

          if (ele !== "div" || button) {
            return;
          }

          setActive(!active);
        }}
        className={styles.card}
      >
        {selected || selectedInternal ? (
          <>
            <img src={Placeholder} alt="Selected Member" />{" "}
            {(selected ?? selectedInternal).Label}
          </>
        ) : (
          <>
            <ClientIcon className={styles.icon} />{" "}
            {label ? label : <>{t("searchMember.assign")}</>}
          </>
        )}
        {(selected || selectedInternal) && (
          <CloseIcon
            style={{ paddingLeft: "5px", paddingRight: "5px" }}
            onClick={() => {
              onChoose(null);
            }}
          />
        )}
        {!isDisabled && (
          <FontAwesomeIcon
            onClick={() => {
              setActive(!active);
            }}
            icon={active ? faCaretUp : faCaretDown}
          />
        )}
      </div>

      {active && (
        <DropdownLogic>
          <div className={`${styles.dropdown}`}>
            {allowSearch && (
              <div className={styles.search}>
                <NewInput
                  focus
                  labelClassName={styles.x}
                  className={styles.d}
                  value={searchValue}
                  label={t("searchMember.searchPerson")}
                  onChange={(v) => setSearchValue(v)}
                />
              </div>
            )}
            <div className={styles.list}>
              {searchedMembers
                .filter((member) => member.UserId)
                .map((member) => (
                  <div
                    onClick={() => {
                      setActive(false);
                      onChoose(member);
                    }}
                    key={member.Id}
                    className={styles.box}
                  >
                    <img src={Placeholder} alt={member.Label} />
                    {member.Label}
                  </div>
                ))}
            </div>
            {searchedMembers.length === 0 && !isLoading && (
              <div className={styles.empty}>
                <NotFoundScreen
                  title={t("emptyStates.notFound")}
                  text={t("emptyStates.noResultsSuffix")}
                  size="normal"
                ></NotFoundScreen>
              </div>
            )}
            {showButtons && (
              <div className={styles.buttons}>
                <HollowButton
                  text={t("common.clear")}
                  onClick={clear}
                ></HollowButton>
                <Button text={t("common.save")} onClick={save}></Button>
              </div>
            )}
          </div>
        </DropdownLogic>
      )}
    </div>
  );
};

export default SearchMemberNew;
